import { PHISHING_USER_STATUS, PHISHING_PAYLOAD_TYPE } from '../../domain/domainCodes.js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowRight,
  Download,
  FileBarChart,
  MailOpen,
  MousePointerClick,
  RefreshCw,
  Send,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react'
import Button from '../../components/Button'
import { useOnChange, useRemembered } from '../../components/listMemory'
import { usePageParam } from '../../components/useListParams'
import Card from '../../components/Card'
import Modal from '../../components/Modal'
import DataTable from '../../components/DataTable'
import FilterBar from '../../components/FilterBar'
import Chart from '../../components/Chart'
import { stageColumnsOption } from '../../components/chartTheme'
import { useTheme } from '../../lib/theme'
import PageHeader from '../../components/PageHeader'
import useGoBack from '../../components/useGoBack'
import PageLoader from '../../components/PageLoader'
import { DeniedState, ErrorState } from '../../components/States'
import StatStrip from '../../components/StatStrip'
import StatusBadge from '../../components/StatusBadge'
import { useToast } from '../../components/toastContext'
import {
  REPORT_USER_SORTABLE,
  exportReport,
  getCampaignReport,
  getPhishingReport,
  listReportUsers,
} from './phishingService'
import { reportStrings as S } from './strings'
import './phishing.css'
import { serverDetail } from '../../lib/i18n'
import { displayDate } from '../../lib/dates'

/**
 * The funnel, in the order a recipient passes through it. The tones are the campaigns list's,
 * kept identical so the same outcome never wears two colours in one product: submitting
 * credentials is the WORST outcome and reporting the phish is the best.
 */
const STAGES = [
  { id: 'sent', icon: Send, tone: 'neutral', series: 'absent' },
  { id: 'opened', icon: MailOpen, tone: 'info', series: 'info' },
  { id: 'clicked', icon: MousePointerClick, tone: 'warning', series: 'incomplete' },
  { id: 'submitted', icon: TriangleAlert, tone: 'danger', series: 'failure' },
  { id: 'reported', icon: ShieldCheck, tone: 'success', series: 'success' },
]

/** `PhishingUserStatus` — the tone each recipient state carries. */
const STATUS_TONE = {
  '-1': 'dark',
  [PHISHING_USER_STATUS.SENT]: 'dark',
  [PHISHING_USER_STATUS.EMAIL_OPENED]: 'info',
  [PHISHING_USER_STATUS.LINK_CLICKED]: 'warning',
  [PHISHING_USER_STATUS.DATA_SUBMITTED]: 'danger',
  [PHISHING_USER_STATUS.USER_REPORTED]: 'success',
}

/**
 * تقارير الإصطياد — the legacy `/PhishingReport`, both of its levels.
 *
 * BUILT, not wired: the two Razor pages it replaces could never render (their view paths
 * omitted the `Phishing/` folder, so every request threw), and there was no React screen at
 * all. One component serves both levels, because they are the same report at two scopes —
 * without `:campaignId` it is the whole programme, with it one campaign plus its recipients.
 *
 * WHAT THE FIVE FIGURES ACTUALLY COUNT is not what their names suggest, and the screen says
 * so in a footnote rather than letting a reader assume: `sent` is the number of recipients
 * enrolled, including any still scheduled; `opened` is cumulative; `clicked` excludes anyone
 * who went on to report it; and `reported` is driven by a date column rather than a status.
 */
/**
 * ONE INSTANCE PER SCOPE. Both routes render this page in the same place, so React used to
 * keep one instance across them — and with it the campaign's report, its remembered list
 * state and the history entry `useGoBack` arrived on. Back to "all campaigns" rendered the
 * programme view from the campaign's report and the screen went white (Taiga #1447), and a
 * campaign opened from the report list sent its arrow somewhere else. Keyed, a scope starts
 * from nothing, the way a direct visit does.
 */
export default function PhishingReportRoute() {
  const { campaignId } = useParams()
  return <PhishingReportPage key={campaignId ?? 'all'} />
}

function PhishingReportPage() {
  const { campaignId } = useParams()
  const navigate = useNavigate()
  /* BOTH SCOPES CARRY AN ARROW, and they mean different things.
     A campaign report was opened FROM somewhere — a row on the campaigns list, or the
     report list — so its arrow is history-aware and returns to that, on the page it was
     left on. The whole-programme report has no single origin, and its own "all campaigns"
     button is how you arrive from a campaign; sending it back through history would just
     bounce between the two reports. It goes to the campaigns list, always. */
  const goBack = useGoBack('/phishing/campaigns')
  const toast = useToast()
  /* The share chart bakes resolved colours into its option — see useTheme. */
  useTheme()

  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  /* WHY THE LOAD FAILED, not merely that there is nothing to draw. Every failure used to end
     at `if (!report) return <PageHeader/>` — a page with its title and nothing else — so a
     403 and a bad campaign id in the address were indistinguishable from each other and from
     an empty report. */
  const [loadError, setLoadError] = useState(null)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [usersLoading, setUsersLoading] = useState(false)
  /* The recipients' own search and page size, kept per CAMPAIGN — a search for one
     campaign's recipient means nothing in another's. The page below is in the address, so
     losing these two left `?page=3` pointing at page 3 of a different, unsearched list;
     the by-campaign table further down needs none of this, since it has no `query` prop
     and DataTable remembers its own box. */
  const [query, setQuery] = useRemembered(campaignId && `phishing-report:${campaignId}:query`, '')
  const [status, setStatus] = useRemembered(campaignId && `phishing-report:${campaignId}:status`, '')
  /* In the address; the campaign is already part of it. */
  const [page, setPage] = usePageParam()
  const [pageSize, setPageSize] = useRemembered(
    campaignId && `phishing-report:${campaignId}:pageSize`,
    25,
  )
  const [refreshKey, setRefreshKey] = useState(0)
  const [selectedUser, setSelectedUser] = useState(null)
  /* The order, owned here with the page and sent to the server. Null is the handler's own
     order — latest event first — which no column shows, so no arrow is drawn. */
  const [sort, setSort] = useRemembered(campaignId && `phishing-report:${campaignId}:sort`, null)
  const clickedLabel = report?.payloadType === PHISHING_PAYLOAD_TYPE.QR_CODE ? S.qrVisited : S.stages.clicked

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    /* THE REPORT BELONGS TO A SCOPE, so it is dropped the moment the scope changes rather
       than left on screen until the next one lands. Holding it crashed the page on the way
       back to "all campaigns": `campaignId` clears first, the programme branch renders
       immediately, and it reads `report.campaigns` — which a CAMPAIGN report does not have.
       DataTable was handed `undefined` for its rows and took the whole app down with it.
       Clearing here means the same header-only frame that a direct visit already shows. */
    setReport(null)
    setLoadError(null)
    const load = campaignId ? getCampaignReport(campaignId) : getPhishingReport()
    load
      .then((loaded) => {
        if (cancelled) return
        setLoadError(null)
        setReport(loaded)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setLoadError(error)
        setReport(null)
        setLoading(false)
        toast.error(serverDetail(error?.message) || S.reportFail.load)
      })
    return () => {
      cancelled = true
    }
  }, [campaignId, toast, refreshKey])

  /* A new search is a new list, so it starts at its first page — on a CHANGE only, or the
     page carried in the address would be thrown away on arrival. */
  useOnChange(() => setPage(1), [query, status])

  /* The recipients, only at campaign scope. `campaignId` is never omitted: without it the
     endpoint answers with recipients from EVERY campaign rather than refusing. */
  useEffect(() => {
    if (!campaignId) return undefined
    let cancelled = false
    setUsersLoading(true)
    listReportUsers({ campaignId, page, pageSize, query, status, sort })
      .then((response) => {
        if (cancelled) return
        setRows(response.rows)
        setSelectedUser((current) => current
          ? response.rows.find((row) => row.id === current.id) ?? null
          : null)
        setTotal(response.total)
        setUsersLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setRows([])
        setTotal(0)
        setUsersLoading(false)
        toast.error(serverDetail(error?.message) || S.reportFail.users)
      })
    return () => {
      cancelled = true
    }
  }, [campaignId, page, pageSize, query, status, sort, toast, refreshKey])

  const refresh = useCallback(() => setRefreshKey((value) => value + 1), [])

  const runExport = useCallback(async () => {
    const outcome = await exportReport(campaignId, query, status)
    if (!outcome.ok) toast.error(serverDetail(outcome.message) || S.reportFail.export)
  }, [campaignId, query, status, toast])

  const campaignColumns = useMemo(
    () => [
      {
        key: 'name',
        header: S.columns.campaign,
        render: (row) => <bdi>{row.name}</bdi>,
      },
      {
        key: 'startedOn',
        header: S.columns.startedOn,
        render: (row) => (
          <span className="ud__date" dir="ltr">
            {displayDate(row.startedOn)}
          </span>
        ),
      },
      {
        key: 'status',
        header: S.columns.status,
        // Localized server-side on this payload, unlike the recipient rows.
        render: (row) => <StatusBadge tone="dark">{row.status}</StatusBadge>,
      },
      ...STAGES.map((stage) => ({
        key: stage.id,
        header: S.stages[stage.id],
        align: 'center',
        value: (row) => row.totals[stage.id],
        render: (row) => {
          const count = row.totals[stage.id]
          if (stage.id === 'submitted') {
            return <span className={count > 0 ? 'ph__bad' : 'ph__nil'}>{count}</span>
          }
          if (stage.id === 'reported') {
            return <span className={count > 0 ? 'ph__good' : 'ph__nil'}>{count}</span>
          }
          return count
        },
      })),
      {
        key: 'open',
        header: <FileBarChart size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '72px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <Button variant="ghost" onClick={() => navigate(`/phishing/report/${row.id}`)}>
            <ArrowRight size={16} strokeWidth={1.75} />
          </Button>
        ),
      },
    ],
    [navigate],
  )

  const userColumns = useMemo(
    () => [
      { key: 'name', header: S.columns.user, render: (row) => <bdi>{row.name || row.username}</bdi> },
      { key: 'email', header: S.columns.email, render: (row) => <span dir="ltr">{row.email}</span> },
      { key: 'department', header: S.columns.department, render: (row) => <bdi>{row.department}</bdi> },
      { key: 'hasClicked', header: clickedLabel, sortable: false,
        render: row => <span className={row.hasClicked ? 'ph__bad' : 'ph__nil'}>{row.hasClicked ? clickedLabel : S.notRecorded}{row.firstClickDate && <small className="d-block" dir="ltr">{displayDate(row.firstClickDate)}</small>}</span> },
      {
        key: 'status',
        header: S.columns.status,
        value: (row) => S.states[row.status] ?? '',
        /* THE REPORTING DATE WINS. A recipient who reported the message is shown as having
           reported it whatever their status number says — the same rule the legacy applies,
           and the reason the two can disagree is that `reported` is tracked by date. */
        render: (row) => {
          const state = row.reportedOn ? 4 : row.status
          return (
            <StatusBadge tone={STATUS_TONE[state] ?? 'dark'}>
              {state === PHISHING_USER_STATUS.LINK_CLICKED && report?.payloadType === PHISHING_PAYLOAD_TYPE.QR_CODE ? clickedLabel : S.states[state] ?? String(row.status)}
            </StatusBadge>
          )
        },
      },
      {
        key: 'phishedCount',
        header: S.columns.phished,
        align: 'center',
        render: (row) => (
          <span className={row.phishedCount > 0 ? 'ph__bad' : 'ph__nil'}>{row.phishedCount}</span>
        ),
      },
      {
        key: 'history',
        header: S.history,
        sortable: false,
        searchable: false,
        render: (row) => <span className={row.failedCampaignCount ? 'ph__bad' : 'ph__nil'} title={S.historyHint}>{S.historyValue(row.failedCampaignCount, row.sentCampaignCount)}</span>,
      },
      {
        key: 'reportedOn',
        header: S.columns.reportedOn,
        render: (row) =>
          row.reportedOn ? (
            <span className="ud__date" dir="ltr">
              {displayDate(row.reportedOn)}
            </span>
          ) : null,
      },
      {
        key: 'details',
        header: S.details,
        sortable: false,
        searchable: false,
        render: (row) => <Button variant="ghost" onClick={() => {
          setSelectedUser(row)
          setRefreshKey((value) => value + 1)
        }}>{S.details}</Button>,
      },
    ],
    [clickedLabel, report?.payloadType],
  )
  /* Only what `FilterPhishingReportUsersHandler` can order by draws an arrow. */
  const sortableColumns = useMemo(
    () =>
      userColumns.map((column) => ({
        ...column,
        sortable: column.sortable !== false && REPORT_USER_SORTABLE.includes(column.key),
      })),
    [userColumns],
  )

  if (loading) {
    return (
      <>
        <PageHeader title={S.title} />
        <PageLoader />
      </>
    )
  }

  if (loadError) {
    return (
      <>
        <PageHeader title={S.title} onBack={campaignId ? goBack : undefined} />
        {loadError.denied ? (
          <DeniedState description={loadError.message} />
        ) : (
          <ErrorState title={S.reportFail.load} description={loadError.message} />
        )}
      </>
    )
  }

  if (!report) return <PageHeader title={S.title} />

  const totals = report.totals
  const base = totals.sent || 0
  const shares = STAGES.map((stage) => {
    const count = totals[stage.id] || 0
    return {
      name: stage.id === 'clicked' ? clickedLabel : S.stages[stage.id],
      count,
      rate: base ? Math.round((count / base) * 1000) / 10 : 0,
      tone: stage.series,
    }
  })

  return (
    <>
      <PageHeader
        onBack={campaignId ? goBack : () => navigate('/phishing/campaigns')}
        title={campaignId ? report.name : S.title}
        actions={
          campaignId ? (
            <div className="ph__actions">
              <Button variant="soft" onClick={refresh}><RefreshCw size={16} strokeWidth={1.75} />{S.refresh}</Button>
              <Button variant="soft" onClick={runExport}><Download size={16} strokeWidth={1.75} />{S.exportExcel}</Button>
            </div>
          ) : null
        }
      />

      <StatStrip
        label={S.funnel}
        items={STAGES.map((stage) => ({
          id: stage.id,
          icon: stage.icon,
          label: stage.id === 'clicked' ? clickedLabel : S.stages[stage.id],
          value: totals[stage.id],
          tone: stage.tone,
          selectable: false,
        }))}
      />

      {/* The same figures as a share of the recipients, because a count alone does not
          say whether nine clicks is most of a campaign or a handful of it. Columns on one
          0–100% axis rather than a funnel: the stages overlap, they do not nest. */}
      <Card title={S.share} subtitle={S.shareHint}>
        <Chart
          option={stageColumnsOption(shares, { total: base, ofLabel: S.shareOf })}
          height={280}
          ariaLabel={S.share}
        />
        <p className="ph__note">{S.countingNote}</p>
      </Card>

      {!campaignId && (
        <Card title={S.byCampaign}>
          <DataTable
            columns={campaignColumns}
            rows={report.campaigns ?? []}
            getRowId={(row) => row.id}
            rowTo={(row) => `/phishing/report/${row.id}`}
            emptyMessage={S.noCampaigns}
            initialSort={{ key: 'startedOn', dir: 'desc' }}
          />
        </Card>
      )}

      {campaignId && (
        <Card title={S.recipients} className="ph__recipients">
          <FilterBar
            search={{ value: query, onChange: setQuery, placeholder: S.columns.user }}
            chips={status === '' ? [] : [{ id: 'status', label: S.columns.status, value: S.states[status], onRemove: () => setStatus('') }]}
          >
            <label className="ph__status-filter">{S.columns.status}
              <select aria-label={S.columns.status} value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="">{S.allStatuses}</option>
                {Object.entries(S.states).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
          </FilterBar>
          <DataTable
            /* A second list on the same screen: the address already belongs to the first. */
            pageParam={null}
            columns={sortableColumns}
            rows={rows}
            loading={usersLoading}
            query={query}
            getRowId={(row) => row.id}
            emptyMessage={S.noUsers}
            server={{
              page,
              pageSize,
              total,
              sort,
              onChange: (next) => {
                setPage(next.page)
                setPageSize(next.pageSize)
                setSort(next.sort)
              },
            }}
          />
        </Card>
      )}

      <Modal open={Boolean(selectedUser)} size="lg" title={S.userDetails} onClose={() => setSelectedUser(null)}>
        {selectedUser && (
          <div className="ph__user-detail">
            <p><bdi>{selectedUser.name || selectedUser.username}</bdi> · <span dir="ltr">{selectedUser.email}</span></p>
            <div className="ph__history-summary">
              <strong>{S.history}: {S.historyValue(selectedUser.failedCampaignCount, selectedUser.sentCampaignCount)}</strong>
              <p>{S.otherFailures(selectedUser.otherFailedCampaignCount)}</p>
              <p className="ph__note">{S.historyHint}</p>
            </div>
            <h3>{S.timeline}</h3>
            {selectedUser.events?.length ? (
              <ol className="ph__timeline">
                {selectedUser.events.map((event, index) => (
                  <li key={`${event.at}-${index}`}>
                    <div><StatusBadge tone={STATUS_TONE[event.status] ?? 'dark'}>{S.states[event.status] ?? String(event.status)}</StatusBadge> <span dir="ltr">{event.at || '—'}</span></div>
                    {event.ip && <p><bdi>{S.ipAddress}: {event.ip}</bdi></p>}
                    {event.userAgent && <p><bdi>{S.userAgent}: {event.userAgent}</bdi></p>}
                    {event.forwardedFor && <p><bdi>{S.forwardedFor}: {event.forwardedFor}</bdi></p>}
                    {renderEventMetadata(event.metadata, S)}
                  </li>
                ))}
              </ol>
            ) : <p className="ph__note">{S.noEvents}</p>}
          </div>
        )}
      </Modal>
    </>
  )
}

function renderEventMetadata(metadata, S) {
  if (!metadata) return null
  let parsed = null
  try {
    parsed = typeof metadata === 'string' ? JSON.parse(metadata) : metadata
  } catch {
    return <p><bdi>{S.metadata}: {metadata}</bdi></p>
  }

  if (!parsed || typeof parsed !== 'object') {
    return <p><bdi>{S.metadata}: {String(parsed)}</bdi></p>
  }

  // Rawam 7 format: { submittedFieldNames: ["username", "department", ...] }
  if (Array.isArray(parsed.submittedFieldNames)) {
    if (parsed.submittedFieldNames.length === 0) return null
    return (
      <div className="ph__meta-block">
        <bdi>{S.submittedFields || S.metadata}:</bdi>
        <span className="ph__meta-tags">
          {parsed.submittedFieldNames.map((name, i) => (
            <span key={i} className="ph__meta-tag" dir="ltr">{name}</span>
          ))}
        </span>
      </div>
    )
  }

  // Key-value pairs (legacy Rawam 6.4 format or future captured form values)
  const entries = Object.entries(parsed).filter(([k]) => k !== 'browser')
  if (entries.length === 0) return null
  return (
    <div className="ph__meta-block">
      <bdi>{S.metadata}:</bdi>
      <dl className="ph__meta-kv">
        {entries.map(([key, val]) => (
          <div key={key} className="ph__meta-row">
            <dt dir="ltr">{key}:</dt>
            <dd dir="ltr">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

